// Admin → Outreach: the ONE Twilio number Atlas texts patients from (Jordan,
// 2026-10-26). Save/clear it, see whether a text would actually go out (secrets,
// number, dry run), copy the two webhook URLs Twilio must point at, and send a
// test message to your own mobile. A practice may override the number on
// Practice Details → Integrations (SmsNumberCard).
//
// Admin-only: the Admin page is reachable only by admins (App.js), and the server
// enforces the role again on every endpoint. No patient data anywhere here.

import React, { useEffect, useRef, useState } from "react";

import {
  fetchSharedSms,
  looksLikePhone,
  saveSharedSms,
  sendSharedTestSms,
} from "./smsNumberApi";
import { SmsMessage, SmsStatus, WebhookList } from "./SmsNumberParts";
import "./SmsNumberCard.css";

export const NO_SHARED_NUMBER = "no shared number saved — save one below";

export default function SharedSmsNumberTab() {
  const [config, setConfig] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [testTo, setTestTo] = useState("");
  const [testBusy, setTestBusy] = useState(false);
  const [testError, setTestError] = useState(null);
  const [testMessage, setTestMessage] = useState(null);
  const busyRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    fetchSharedSms()
      .then((c) => {
        if (!cancelled) setConfig(c);
      })
      .catch((err) => {
        if (!cancelled) setLoadError(err.message);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const startEdit = () => {
    setDraft(config.sharedNumber || "");
    setError(null);
    setMessage(null);
    setEditing(true);
  };

  const save = async (value) => {
    if (busyRef.current) return;
    const trimmed = String(value || "").trim();
    if (trimmed && !looksLikePhone(trimmed)) {
      setError("Enter the number as +1 then the digits (E.164), or a 10-digit US number.");
      return;
    }
    busyRef.current = true;
    setBusy(true);
    setError(null);
    try {
      const saved = await saveSharedSms(trimmed);
      setConfig(saved);
      setEditing(false);
      setMessage(
        saved.sharedNumber
          ? `Saved. Patients are texted from ${saved.sharedNumber}.`
          : "Cleared. With no shared number, only practices with an override can text.",
      );
    } catch (err) {
      setError(err.message || "Could not save the shared SMS number.");
    } finally {
      busyRef.current = false;
      setBusy(false);
    }
  };

  const sendTest = async () => {
    if (testBusy) return;
    const to = testTo.trim();
    if (!looksLikePhone(to)) {
      setTestError("Enter your mobile as +1 then the digits, or a 10-digit US number.");
      return;
    }
    setTestBusy(true);
    setTestError(null);
    setTestMessage(null);
    try {
      const result = await sendSharedTestSms(to);
      if (result.dryRun) {
        setTestMessage(`Dry run: nothing was sent to ${result.toMasked}.`);
      } else if (result.sent) {
        setTestMessage(`Test message sent to ${result.toMasked}.`);
      } else {
        setTestError("The server did not send the test message.");
      }
    } catch (err) {
      setTestError(err.message || "Could not send the test message.");
    } finally {
      setTestBusy(false);
    }
  };

  if (loadError) {
    return (
      <div className="sms-card">
        <div className="sms-card-head">
          <h3>Shared SMS number</h3>
        </div>
        <SmsMessage tone="error">{loadError}</SmsMessage>
      </div>
    );
  }
  if (!config) {
    return (
      <div className="sms-card">
        <div className="sms-card-head">
          <h3>Shared SMS number</h3>
        </div>
        <p className="sms-muted">Loading…</p>
      </div>
    );
  }

  const testDisabled = !config.smsConfigured || testBusy;
  const testReason = !config.twilioConfigured
    ? "Twilio credentials are not set on the server."
    : !config.sharedNumber
      ? "No shared number is saved."
      : !config.smsConfigured
        ? "SMS is not configured."
        : null;

  return (
    <div className="sms-card">
      <div className="sms-card-head">
        <h3>Shared SMS number</h3>
        {!editing && (
          <button type="button" className="sms-btn" onClick={startEdit}>
            {config.sharedNumber ? "Edit" : "Set number"}
          </button>
        )}
      </div>
      <p className="sms-intro">
        The one Twilio number patients text and are texted from (patient outreach),
        for every practice. A practice can save its own number on Practice Details →
        Integrations, which takes precedence for that practice only.
      </p>

      <SmsStatus status={config} number={config.sharedNumber} noNumberReason={NO_SHARED_NUMBER} />

      <SmsMessage tone="success" onDismiss={() => setMessage(null)}>
        {message}
      </SmsMessage>

      {editing ? (
        <div className="sms-edit">
          <SmsMessage tone="error" onDismiss={() => setError(null)}>
            {error}
          </SmsMessage>
          <div className="sms-field">
            <label htmlFor="sms-shared-number">Shared number</label>
            <input
              id="sms-shared-number"
              className="sms-input"
              type="tel"
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              placeholder="+14155550123"
              autoComplete="off"
              disabled={busy}
            />
          </div>
          <div className="sms-actions">
            <button type="button" className="sms-btn" onClick={() => setEditing(false)} disabled={busy}>
              Cancel
            </button>
            <button type="button" className="sms-btn sms-btn-primary" onClick={() => save(draft)} disabled={busy}>
              {busy ? "Saving…" : "Save"}
            </button>
            {config.sharedNumber && (
              <button type="button" className="sms-btn sms-btn-danger" onClick={() => save("")} disabled={busy}>
                Clear number
              </button>
            )}
          </div>
        </div>
      ) : (
        <div className="sms-number-row">
          {config.sharedNumber ? (
            <span className="sms-number">{config.sharedNumber}</span>
          ) : (
            <span className="sms-muted">No shared number saved.</span>
          )}
        </div>
      )}

      <WebhookList webhooks={config.webhooks} />

      <h4 className="sms-subhead">Test</h4>
      <p className="sms-intro">
        Sends one short message from the shared number to your own mobile so you can
        confirm Twilio is wired up. Nothing is recorded against a patient.
      </p>
      <SmsMessage tone="success" onDismiss={() => setTestMessage(null)}>
        {testMessage}
      </SmsMessage>
      <SmsMessage tone="error" onDismiss={() => setTestError(null)}>
        {testError}
      </SmsMessage>
      <div className="sms-test">
        <div className="sms-field">
          <label htmlFor="sms-test-to">Your mobile</label>
          <input
            id="sms-test-to"
            className="sms-input"
            type="tel"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="+14155550123"
            autoComplete="off"
            disabled={testDisabled}
          />
        </div>
        <button type="button" className="sms-btn sms-btn-primary" onClick={sendTest} disabled={testDisabled}>
          {testBusy ? "Sending…" : "Send test SMS to me"}
        </button>
      </div>
      {testReason && <p className="sms-muted">Test send is disabled: {testReason}</p>}
    </div>
  );
}
